import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { DEFAULT_PAGE_LOAD_SIZE } from '../../../api/pagination';
import {
    searchCoursesByCodeOrName,
    searchCoursesByLoggedInUser,
    setCurrentCourse
} from '../../../store/courseService';
import { selectLocationIdsAndNamesLength } from '../../../store/dataSelectors';
import { loadLocationIdsAndNames } from '../../../store/directusService';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../utils/formValidation/commonConstants';
import { stableSort } from '../../../utils/sortFunctions';
import {
    ADVISER,
    hasRole,
    LOCAL_ADMIN,
    MANAGER,
    QUALITY,
    SELF_EMPLOYED_COACH,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import SearchOnEnter from '../../search/SearchOnEnter';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';

import CourseInstanceTableRow from './CourseInstanceTableRow';

import classes from '../courseStyles/courseToolbar.module.css';

const loadPageSize = DEFAULT_PAGE_LOAD_SIZE;

const headCells = [
    { id: 'code', numeric: false, label: 'ID' },
    { id: 'name', numeric: false, label: 'Course Name' },
    { id: 'warning', numeric: true, label: 'Missing Course Info', sortable: false },
    { id: 'endDate', numeric: true, label: 'End Date' },
    { id: 'startDate', numeric: true, label: 'Start Date' },
    { id: 'edit', numeric: true, label: '', sortable: false }
];

const initialRowMetaData = {
    order: 'desc',
    orderBy: 'endDate',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const acceptedRoles = [ADVISER, MANAGER, QUALITY, SUPERUSER];
const extendedRoles = [...acceptedRoles, LOCAL_ADMIN];
const attendanceAccessRoles = [...acceptedRoles, SELF_EMPLOYED_COACH];

const CourseInstanceManagement = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [internalCourses, setInternalCourses] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [coursesLoaded, setCoursesLoaded] = useState(false);
    const [totalResults, setTotalResults] = useState(0);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [openId, setOpenId] = useState('');
    const [isDisabled, setIsDisabled] = useState(true);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [isAttendanceAccessDisabled, setIsAttendanceAccessDisabled] = useState(true);
    const [isSelfEmployedCoach, setIsSelfEmployedCoach] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const usersCourses = useSelector((state) => state.entities.courseService.usersCourses);
    const usersCoursesMetaData = useSelector(
        (state) => state.entities.courseService.usersCoursesMetaData
    );
    const usersCoursesByCodeOrName = useSelector(
        (state) => state.entities.courseService.usersCoursesByCodeOrName
    );
    const locationIdsAndNamesLength = useSelector(selectLocationIdsAndNamesLength);

    // USEEFFECTS
    useEffect(() => {
        locationIdsAndNamesLength < 1 && dispatch(loadLocationIdsAndNames());
    }, []);

    useEffect(() => {
        if (!loggedInUser || !('id' in loggedInUser)) return;
        dispatch(
            searchCoursesByLoggedInUser({
                courseModuleUserId: loggedInUser.id
            })
        );
        setIsDisabled(!hasRole(acceptedRoles, roles));
        setIsButtonDisabled(!hasRole(extendedRoles, roles));
        setIsAttendanceAccessDisabled(!hasRole(attendanceAccessRoles, roles));
        setIsSelfEmployedCoach(hasRole([SELF_EMPLOYED_COACH], roles));
    }, [loggedInUser, roles]);

    useEffect(() => {
        if (!coursesLoaded) return;
        const internalCourses = usersCourses.filter((el) => el.courseProvider === 'internal');

        if (internalCourses.length < 1 && !usersCoursesMetaData?.last)
            dispatch(
                searchCoursesByLoggedInUser(
                    { courseModuleUserId: loggedInUser.id },
                    usersCoursesMetaData?.number + 1
                )
            );
        else setCoursesLoaded(false);

        setInternalCourses(internalCourses);
        setRows(internalCourses);
    }, [usersCourses, coursesLoaded, usersCoursesMetaData]);

    useEffect(() => {
        setTotalResults(usersCoursesMetaData.totalElements);
    }, [usersCoursesMetaData]);

    useEffect(() => {
        if (!coursesLoaded || !searchTerm) return;
        if (usersCoursesByCodeOrName.length < 1) setRows([]);
        setCoursesLoaded(false);
    }, [usersCoursesByCodeOrName, coursesLoaded]);

    useEffect(() => {
        if (
            successMessage === 'Course records by logged in user have been loaded' ||
            successMessage === 'Course search record has been loaded' ||
            successMessage.includes(`Module details have been updated for module`)
        )
            setCoursesLoaded(true);
    }, [successMessage]);

    useEffect(() => {
        if (!searchTerm) {
            setRows(internalCourses);
            setTotalResults(usersCoursesMetaData.totalElements);
        }
    }, [searchTerm]);

    // HELPER FNS
    const createRows = () => {
        return stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <CourseInstanceTableRow
                key={el.id}
                row={el}
                isDisabled={isDisabled}
                isAttendanceAccessDisabled={isAttendanceAccessDisabled}
                isSelfEmployedCoach={isSelfEmployedCoach}
                toggleDropdown={onToggleDropdown}
                openId={openId}
                onEditRow={onEditRow}
            />
        ));
    };

    // EVENT HANDLERS
    const onTermChange = (value) => setSearchTerm(value);

    const onSearch = () => {
        if (!searchTerm) {
            setTotalResults(usersCoursesMetaData.totalElements);
            return;
        }
        setTotalResults(1);
        if (internalCourses.length) {
            const filtered = internalCourses.filter(
                (el) =>
                    el.name.toLowerCase() === searchTerm.toLowerCase() ||
                    el.code.toLowerCase() === searchTerm.toLowerCase()
            );
            filtered.length
                ? setRows(filtered)
                : dispatch(
                      searchCoursesByCodeOrName({
                          courseModuleUserId: loggedInUser.id,
                          term: searchTerm
                      })
                  );
        }
    };

    const onEditRow = (e, row) => {
        e.stopPropagation();
        dispatch(setCurrentCourse(row));
        navigate('/edit_course', { state: { id: row.id, accordionPanel: 'courses' } });
    };

    const onToggleDropdown = (id) => setOpenId(id);

    const onSearchAllActiveTemplates = () => navigate('/all_active_course_templates');

    // RENDER
    return (
        <>
            <div className={classes.coursesToolBar} data-testid="my_courses_toolbar">
                <SearchOnEnter
                    searchTerm={searchTerm}
                    placeholder="Enter search term..."
                    search={onSearch}
                    onChange={onTermChange}
                />
                <Button
                    id="SearchAllActiveTemplatesButton"
                    disabled={isButtonDisabled}
                    content="Search All Active Course Templates"
                    icon={true}
                    onClick={onSearchAllActiveTemplates}
                />
            </div>

            {usersCoursesMetaData.last && rows.length < 1 ? (
                <LoadingSpinner content="No courses found" />
            ) : (
                <ResultsTable
                    defaultOrderBy={'endDate'}
                    sortOrder={'desc'}
                    headCells={headCells}
                    loadResults={searchCoursesByLoggedInUser}
                    passRowMetaDataUp={setRowMetaData}
                    apiParams={{ courseModuleUserId: loggedInUser.id }}
                    tableRows={createRows()}
                    totalResults={totalResults}
                    metaData={usersCoursesMetaData}
                    loadPageSize={loadPageSize}
                />
            )}
        </>
    );
};

CourseInstanceManagement.propTypes = {
    isOpen: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
};

export default CourseInstanceManagement;
